import React from "react";
import { format } from "date-fns";
import { th, enUS } from "date-fns/locale";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/shared/components/ui/sheet";
import { Button } from "@/shared/components/ui/button";
import { Badge } from "@/shared/components/ui/badge";
import StatusBadge from "./StatusBadge";
import {
  Copy,
  Check,
  ArrowDownToLine,
  ArrowUpFromLine,
  Clock,
  CheckCircle2,
  XCircle,
  Loader2,
} from "lucide-react";
import { useLanguage } from "@/shared/components/shared/LanguageContext";

export default function TransactionDetailPanel({ transaction, open, onClose }) {
  let t = null;
  let language = "en";
  try {
    const langContext = useLanguage();
    t = langContext?.t;
    language = langContext?.language || "en";
  } catch (e) {
    // Not wrapped in LanguageProvider
  }
  
  const translate = (key) => {
    if (t) return t(key);
    const fallbacks = {
      amount: "Amount",
      transactionId: "Transaction ID",
      provider: "Provider",
      reference: "Reference",
      paymentMethod: "Payment Method",
      currency: "Currency",
      statusTimeline: "Status Timeline",
      createdLabel: "Created",
      processingLabel: "Processing",
      completedLabel: "Completed",
      failedLabel: "Failed",
      callbackStatus: "Callback Status",
      attempts: "Attempts",
      webhooks: "Webhooks",
      error: "Error",
      fee: "Fee",
      netAmount: "Net Amount",
      deposit: "Deposit",
      withdrawal: "Withdrawal",
    };
    return fallbacks[key] || key;
  };
  
  // Helper to translate transaction type
  const getTransactionTypeLabel = (type) => {
    if (!type) return "";
    if (t) {
      return type === "deposit" ? t("deposit") : t("withdrawal");
    }
    return type === "deposit" ? translate("deposit") : translate("withdrawal");
  };
  const [copied, setCopied] = React.useState(false);

  const copyId = () => {
    navigator.clipboard.writeText(transaction?.transaction_id || "");
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  if (!transaction) return null;

  const statusTimeline = [
    { status: "created", label: translate("createdLabel"), time: transaction.created_date, done: true },
    { 
      status: "processing", 
      label: translate("processingLabel"), 
      time: transaction.processed_at,
      done: ["processing", "success", "failed"].includes(transaction.status)
    },
    { 
      status: transaction.status === "failed" ? "failed" : "success", 
      label: transaction.status === "failed" ? translate("failedLabel") : translate("completedLabel"),
      time: transaction.completed_at,
      done: ["success", "failed"].includes(transaction.status)
    },
  ];

  return (
    <Sheet open={open} onOpenChange={onClose}>
      <SheetContent className="w-full sm:max-w-lg overflow-y-auto">
        <SheetHeader className="pb-4 border-b">
          <SheetTitle className="flex items-center gap-3">
            <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
              transaction.type === "deposit" ? "bg-blue-100" : "bg-emerald-100"
            }`}>
              {transaction.type === "deposit" ? (
                <ArrowDownToLine className="h-5 w-5 text-blue-600" />
              ) : (
                <ArrowUpFromLine className="h-5 w-5 text-emerald-600" />
              )}
            </div>
            <div>
              <p className="text-lg capitalize">{getTransactionTypeLabel(transaction.type)}</p>
              <StatusBadge status={transaction.status} />
            </div>
          </SheetTitle>
        </SheetHeader>

        <div className="py-6 space-y-6">
          {/* Amount */}
          <div className="text-center py-4 bg-slate-50 rounded-xl">
            <p className="text-sm text-slate-500 mb-1">{translate("amount")}</p>
            <p className={`text-3xl font-bold ${
              transaction.type === "deposit" ? "text-blue-600" : "text-emerald-600"
            }`}>
              ฿{transaction.amount?.toLocaleString()}
            </p>
            {transaction.fee_amount && (
              <p className="text-sm text-slate-400 mt-1">
                {translate("fee")}: ฿{transaction.fee_amount?.toLocaleString()} • {translate("netAmount")}: ฿{transaction.net_amount?.toLocaleString()}
              </p>
            )}
          </div>

          {/* Transaction ID */}
          <div>
            <p className="text-sm text-slate-500 mb-2">{translate("transactionId")}</p>
            <div className="flex items-center gap-2">
              <code className="flex-1 bg-slate-100 px-3 py-2 rounded-lg font-mono text-sm">
                {transaction.transaction_id}
              </code>
              <Button variant="outline" size="icon" onClick={copyId}>
                {copied ? <Check className="h-4 w-4 text-emerald-500" /> : <Copy className="h-4 w-4" />}
              </Button>
            </div>
          </div>

          {/* Details Grid */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-sm text-slate-500">{translate("provider")}</p>
              <p className="font-medium">{transaction.provider_code || "N/A"}</p>
            </div>
            <div>
              <p className="text-sm text-slate-500">{translate("reference")}</p>
              <p className="font-medium">{transaction.merchant_ref || "N/A"}</p>
            </div>
            <div>
              <p className="text-sm text-slate-500">{translate("paymentMethod")}</p>
              <p className="font-medium capitalize">{transaction.payment_method?.replace(/_/g, " ") || "N/A"}</p>
            </div>
            <div>
              <p className="text-sm text-slate-500">{translate("currency")}</p>
              <p className="font-medium">{transaction.currency || "THB"}</p>
            </div>
          </div>

          {/* Status Timeline */}
          <div>
            <p className="text-sm text-slate-500 mb-3">{translate("statusTimeline")}</p>
            <div className="space-y-4">
              {statusTimeline.map((step, index) => {
                const isLast = index === statusTimeline.length - 1;
                const isFailed = step.status === "failed";
                
                return (
                  <div key={step.status} className="flex gap-3">
                    <div className="flex flex-col items-center">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                        step.done
                          ? isFailed
                            ? "bg-red-100"
                            : "bg-emerald-100"
                          : "bg-slate-100"
                      }`}>
                        {step.done ? (
                          isFailed ? (
                            <XCircle className="h-4 w-4 text-red-600" />
                          ) : (
                            <CheckCircle2 className="h-4 w-4 text-emerald-600" />
                          )
                        ) : (
                          <Clock className="h-4 w-4 text-slate-400" />
                        )}
                      </div>
                      {!isLast && (
                        <div className={`w-0.5 h-8 ${step.done ? "bg-emerald-200" : "bg-slate-200"}`} />
                      )}
                    </div>
                    <div className="flex-1 pb-2">
                      <p className={`font-medium ${step.done ? "text-slate-900" : "text-slate-400"}`}>
                        {step.label}
                      </p>
                      {step.time && step.done && (
                        <p className="text-sm text-slate-500">
                          {format(new Date(step.time), language === "th" ? "d MMM yyyy HH:mm:ss" : "MMM d, yyyy HH:mm:ss", { locale: language === "th" ? th : enUS })}
                        </p>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Callback Status */}
          <div>
            <p className="text-sm text-slate-500 mb-2">{translate("callbackStatus")}</p>
            <div className="flex items-center gap-2">
              <Badge className={
                (() => {
                  const statusLower = (transaction.callback_status || "pending").toLowerCase();
                  return statusLower === "success" || statusLower === "delivered" 
                    ? "bg-emerald-100 text-emerald-700"
                    : statusLower === "failed" 
                    ? "bg-red-100 text-red-700"
                    : "bg-amber-100 text-amber-700";
                })()
              }>
                {(() => {
                  const status = (transaction.callback_status || "pending").toLowerCase();
                  if (!t) {
                    // Return human-readable status even without translation
                    if (status === "success" || status === "delivered") return "Delivered";
                    if (status === "failed") return "Failed";
                    return "Pending";
                  }
                  // Map backend status values to translation keys for callback status
                  if (status === "success" || status === "delivered") return t("delivered") || "Delivered";
                  if (status === "failed") return t("failed") || "Failed";
                  if (status === "pending") return t("pending") || "Pending";
                  return status;
                })()}
              </Badge>
              {transaction.callback_attempts > 0 && (
                <span className="text-sm text-slate-500">
                  ({transaction.callback_attempts} {translate("attempts")})
                </span>
              )}
            </div>
          </div>

          {/* Webhook Log Preview */}
          {transaction.webhook_data && (
            <div>
              <p className="text-sm text-slate-500 mb-2">{translate("webhooks")}</p>
              <pre className="bg-slate-900 text-slate-100 p-4 rounded-xl text-xs overflow-x-auto max-h-40">
                {transaction.webhook_data}
              </pre>
            </div>
          )}

          {/* Error Message */}
          {transaction.error_message && (
            <div className="p-4 bg-red-50 border border-red-200 rounded-xl">
              <p className="text-sm font-medium text-red-800">{translate("error")}</p>
              <p className="text-sm text-red-700 mt-1">{transaction.error_message}</p>
            </div>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}